'use memo'
/** @jsxImportSource @emotion/react */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import req from '@sportninja/common/api/request'
import { useRosterSelect } from 'src/components/List/RosterSelectNew'
import { rosterReducer, initialRosterState } from './reducer'
import { RosterContextValue, RosterActionType } from './types'

const RosterContext = createContext<RosterContextValue | undefined>(undefined)

interface RosterProviderProps {
  teamId: string
  onComplete: (data: any) => void
  existingRosterIdFromLocationState?: string
  refreshKey?: number
  children: React.ReactNode
}

export const RosterProvider: React.FC<RosterProviderProps> = ({
  teamId,
  onComplete,
  existingRosterIdFromLocationState,
  refreshKey,
  children,
}) => {
  const [state, dispatch] = useReducer(rosterReducer, initialRosterState)
  const [refreshingRosterData, setRefreshingRosterData] = useState(false)

  const {
    loading: loadingRosters,
    rosters,
    selectedRosterId: hookSelectedRosterId,
    setSelectedRosterId: hookSetSelectedRosterId,
    loadRosters,
  } = useRosterSelect(teamId, existingRosterIdFromLocationState)

  // Sync rosters from hook to state
  useEffect(() => {
    dispatch({ type: RosterActionType.SET_ROSTERS, payload: rosters })
  }, [rosters])

  useEffect(() => {
    dispatch({
      type: RosterActionType.SET_LOADING_ROSTERS,
      payload: loadingRosters,
    })
  }, [loadingRosters])

  useEffect(() => {
    if (hookSelectedRosterId) {
      dispatch({
        type: RosterActionType.SET_SELECTED_ROSTER,
        payload: hookSelectedRosterId,
      })
    }
  }, [hookSelectedRosterId])

  const hasLoadedOnce = useRef(false)

  const fetchRosterData = useCallback(
    async (rosterId: string) => {
      // Only show full loading spinner on initial load, not on refreshes
      if (!hasLoadedOnce.current) {
        dispatch({ type: RosterActionType.SET_LOADING, payload: true })
      }
      try {
        const [playersRes, officialsRes] = await Promise.all([
          req(`/teams/${teamId}/rosters/${rosterId}/players`),
          req(`/teams/${teamId}/rosters/${rosterId}/team-officials`),
        ])

        const rosterObj: any = rosters.find((r: any) => r.id === rosterId)
        const lockRosterDate = rosterObj?.lock_roster_date || null

        const allPlayers = playersRes.data?.players || []
        const players: any[] = []
        const affiliates: any[] = []
        allPlayers.forEach((p: any) => {
          const player = {
            ...p,
            isFromTeamRoster: true,
            isEmailRequired: playersRes.data?.require_email,
          }
          if (player.is_affiliate) {
            affiliates.push(player)
          } else {
            players.push(player)
          }
        })

        const permissions = playersRes.meta?.permissions || {}
        const teamPermissions = permissions[teamId] || {}

        dispatch({
          type: RosterActionType.SET_ROSTER_DATA,
          payload: {
            roster: {
              ...playersRes.data,
              team_officials: officialsRes.data?.team_officials || [],
            },
            players,
            affiliates,
            teamOfficials: officialsRes.data?.team_officials || [],
            permissions: teamPermissions,
            lockRosterDate,
            canAddPlayersFromRoster:
              playersRes.data?.can_add_players_from_roster || false,
            isRegistrationOn: playersRes.data?.is_registration_on || false,
            requireEmail: playersRes.data?.require_email || false,
            shouldShowWaiver: playersRes.data?.should_show_waiver || false,
          },
        })

        hasLoadedOnce.current = true

        onComplete({
          id: rosterId,
          lock_roster_date: lockRosterDate,
          permissions,
          players: allPlayers,
          can_add_players_from_roster:
            playersRes.data?.can_add_players_from_roster || false,
          is_registration_on: playersRes.data?.is_registration_on || false,
          require_email: playersRes.data?.require_email || false,
          should_show_waiver: playersRes.data?.should_show_waiver || false,
        })
      } catch (e: any) {
        dispatch({
          type: RosterActionType.SET_ERROR,
          payload: e?.message || 'Failed to load roster data',
        })
      }
    },
    [teamId, rosters, onComplete]
  )

  useEffect(() => {
    if (state.selectedRosterId?.length > 0 && rosters.length > 0) {
      fetchRosterData(state.selectedRosterId)
    }
  }, [state.selectedRosterId, rosters])

  // Re-fetch when refreshKey changes (triggered from outside the provider tree)
  const prevRefreshKey = useRef(refreshKey)
  useEffect(() => {
    if (refreshKey !== prevRefreshKey.current) {
      prevRefreshKey.current = refreshKey
      if (state.selectedRosterId?.length > 0) {
        fetchRosterData(state.selectedRosterId)
      }
    }
  }, [refreshKey, state.selectedRosterId, fetchRosterData])

  useEffect(() => {
    if (!loadingRosters && rosters.length === 0) {
      dispatch({ type: RosterActionType.SET_LOADING, payload: false })
    }
  }, [loadingRosters, rosters])

  const refresh = useCallback(async () => {
    if (state.selectedRosterId) {
      try {
        setRefreshingRosterData(true)
        await fetchRosterData(state.selectedRosterId)
      } finally {
        setRefreshingRosterData(false)
      }
    }
  }, [state.selectedRosterId, fetchRosterData])

  const setSelectedRosterId = useCallback(
    (id: string) => {
      hookSetSelectedRosterId(id)
      dispatch({ type: RosterActionType.SET_SELECTED_ROSTER, payload: id })
    },
    [hookSetSelectedRosterId]
  )

  const value: RosterContextValue = useMemo(
    () => ({
      ...state,
      refresh,
      refreshingRosterData,
      setSelectedRosterId,
      loadRosters,
    }),
    [state, refresh, setSelectedRosterId, loadRosters, refreshingRosterData]
  )

  return (
    <RosterContext.Provider value={value}>{children}</RosterContext.Provider>
  )
}

export const useRosterContext = () => {
  const context = useContext(RosterContext)
  if (context === undefined) {
    throw new Error('useRosterContext must be used within RosterProvider')
  }
  return context
}
